import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const UPDATED_AT = '3 July 2026';
const CONTACT_EMAIL = 'siyamcela@shiftwagon.co.za';

const terms = [
  {
    title: 'Platform role',
    body:
      'ShiftWagon provides software that helps customers request transport, drivers and transport partners receive or manage work, and platform administrators support operations. Transport services are performed by drivers, individual owner-drivers, company-linked drivers, or fleet partners approved on the platform.',
  },
  {
    title: 'Accounts and eligibility',
    body:
      'You must provide accurate account information and keep your login credentials secure. Drivers must complete required verification, consent, document, vehicle, banking, and review steps before going online or accepting work. Company owners must provide accurate company and payout information where relevant.',
  },
  {
    title: 'Bookings and service performance',
    body:
      'Customers are responsible for entering accurate pickup, dropoff, contact, load, scheduling, and access details. Drivers are responsible for reviewing available trip details, following applicable road and safety laws, communicating professionally, and completing accepted services according to platform workflow.',
  },
  {
    title: 'Goods, cargo, and prohibited items',
    body:
      'Customers are responsible for describing goods accurately, ensuring the goods are lawful to transport, and not requesting transport of prohibited, unsafe, stolen, hazardous, perishable, fragile, unusually valuable, cash-equivalent, or regulated items unless ShiftWagon has expressly allowed that category in writing. ShiftWagon may refuse, cancel, suspend, or investigate a booking where the load appears unsafe, unlawful, misdescribed, or outside platform rules.',
  },
  {
    title: 'Packing, loading, and access responsibilities',
    body:
      'Customers remain responsible for preparing goods for transport, including suitable packaging, protection, labelling, access instructions, loading assistance where needed, and ensuring pickup and dropoff locations are safe and legally accessible. Drivers and transport partners are responsible for handling accepted trips with reasonable care, but ShiftWagon does not independently inspect every item, package, route condition, or location risk before a trip is accepted.',
  },
  {
    title: 'Transport provider responsibility',
    body:
      'ShiftWagon operates the software platform and support workflow. Transport services are performed by independent drivers, owner-drivers, company-linked drivers, or fleet partners approved on the platform. To the fullest extent permitted by applicable law, Mike Innovations (Pty) Ltd and ShiftWagon are not responsible for goods loss, theft, breakage, leakage, delay, spoilage, missing items, property damage, personal injury, or other harm caused by a driver, transport partner, customer, third party, road event, weather event, incorrect booking information, unsafe packaging, or circumstances outside ShiftWagon platform control.',
  },
  {
    title: 'Location and trip tracking',
    body:
      'The customer app uses location for pickup, dropoff, saved places, route display, and live trip visibility. The driver app uses availability and trip location so the platform can match drivers, show trip progress, estimate arrival, support operations, and protect service integrity. Driver background location is intended for active trips only.',
  },
  {
    title: 'Payments, authorizations, refunds, and payouts',
    body:
      'Customer card details are entered through a secure hosted payment provider. ShiftWagon stores tokenized card references and masked metadata, not raw card numbers or CVV. Bookings may require a saved card, preauthorization, capture, reversal, or refund depending on the operational status. Driver or company payouts may be handled through Paystack subaccount settlement or other approved payout processes.',
  },
  {
    title: 'Insurance and cargo cover',
    body:
      'Driver or vehicle onboarding may include insurance-related information, but this does not mean every booking, load, loss, or damage event is insured by ShiftWagon or Mike Innovations (Pty) Ltd. Customers should arrange their own insurance for valuable, fragile, business-critical, or sensitive goods. Any insurance claim, if available, may depend on the driver, transport partner, insurer, policy terms, exclusions, evidence, excesses, and applicable law.',
  },
  {
    title: 'Documents and verification',
    body:
      'Drivers and company owners may be asked for identity, banking, vehicle, company, and compliance documents. ShiftWagon may review, approve, reject, expire, or request updates to these records. Submitting false, outdated, or misleading information may lead to suspension or account closure.',
  },
  {
    title: 'Messaging and support',
    body:
      'In-app messaging, support tickets, notifications, and attachments must be used only for platform-related coordination, safety, support, and service completion. Abusive, unlawful, misleading, or unrelated use may result in moderation, restricted access, or termination.',
  },
  {
    title: 'Cancellations and operational holds',
    body:
      'Trips may be cancelled, expired, archived, placed on hold, or blocked where payment, safety, compliance, driver availability, route, fraud, support, or operational requirements are not met. Refund or reversal eligibility depends on trip status, payment state, and applicable business policy.',
  },
  {
    title: 'Claims, disputes, and evidence',
    body:
      'Users must report any suspected goods loss, damage, theft, accident, payment issue, safety issue, or service dispute as soon as reasonably possible through ShiftWagon support and provide available evidence such as photos, messages, receipts, delivery notes, item descriptions, police case numbers, insurance information, and witness details. ShiftWagon may assist with platform records and communication, but support assistance does not create an admission of liability by ShiftWagon or Mike Innovations (Pty) Ltd.',
  },
  {
    title: 'Limitation of liability',
    body:
      'To the fullest extent permitted by applicable South African law, ShiftWagon and Mike Innovations (Pty) Ltd will not be liable for indirect, special, consequential, punitive, business-interruption, loss-of-profit, loss-of-revenue, loss-of-goodwill, loss-of-data, goods-loss, cargo-damage, or third-party transport-performance claims arising from use of the platform or a transport service. Nothing in these terms excludes or limits liability that cannot legally be excluded or limited, including rights under applicable consumer protection law.',
  },
  {
    title: 'Indemnity',
    body:
      "You agree to protect and hold ShiftWagon, Mike Innovations (Pty) Ltd, its directors, employees, contractors, and service providers harmless from claims, losses, damages, penalties, costs, and expenses arising from your unlawful use of the platform, inaccurate booking or account information, unsafe or prohibited goods, inadequate packaging, breach of these terms, misuse of another user's information, or conduct that causes harm to another user, driver, transport partner, third party, or the platform.",
  },
  {
    title: 'Acceptable use',
    body:
      'You may not misuse the platform, interfere with systems, attempt unauthorized access, abuse promotions, harass other users, upload unlawful content, falsify location or documents, bypass payment controls, or use customer or driver data outside the purpose of completing or supporting a ShiftWagon service.',
  },
  {
    title: 'Availability and changes',
    body:
      'ShiftWagon may change features, pricing logic, operating areas, driver requirements, payment rules, support workflows, or policies as the platform evolves. We aim to keep the service available, but downtime, maintenance, provider failures, network issues, and operational interruptions may occur.',
  },
  {
    title: 'Legal terms',
    body:
      'These terms are intended to support production launch readiness and app-store disclosure. They should be reviewed by legal counsel before final launch. Nothing on this page limits rights that cannot be limited under applicable South African law.',
  },
];

const TermsPage: React.FC = () => {
  return (
    <div className="landing-shell">
      <Header />
      <main>
        <section className="landing-hero landing-hero-simple policy-hero">
          <div className="container policy-hero-content">
            <span className="landing-badge">Terms of Service</span>
            <h1 className="landing-heading">The rules for using ShiftWagon services.</h1>
            <p className="landing-subheading">
              Last updated: {UPDATED_AT}. These terms apply to ShiftWagon customer, driver,
              company owner, web, and support experiences.
            </p>
          </div>
        </section>

        <section className="landing-section policy-section">
          <div className="container policy-layout">
            <aside className="policy-summary">
              <h2>Service Terms</h2>
              <p>
                These terms describe account responsibilities, bookings, location tracking,
                payment handling, driver verification, support, and acceptable use.
              </p>
              <a className="landing-primary-action" href={`mailto:${CONTACT_EMAIL}`}>
                Contact Us
              </a>
            </aside>

            <div className="policy-content">
              {terms.map((item) => (
                <article className="policy-card" key={item.title}>
                  <h2>{item.title}</h2>
                  <p>{item.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default TermsPage;
